import type { TFunction } from 'i18next';
import type { EtsyPendingOrder } from '@/services/etsyService';

const ASPECT_RATIO_WARNING_THRESHOLD = 0.03;

const formatRatio = (ratio: number): string => (
  ratio >= 10 ? ratio.toFixed(1) : ratio.toFixed(2)
);

const formatPixels = (value: number): string => Math.round(value).toLocaleString();

const formatInches = (value: number): string => (
  value >= 10 ? value.toFixed(1) : value.toFixed(2)
);

const isPositiveNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value > 0
);

export type EtsyProductionWarning = {
  readonly itemId: string;
  readonly itemTitle: string;
  readonly uploadedWidthPx: number;
  readonly uploadedHeightPx: number;
  readonly targetWidthIn: number;
  readonly targetHeightIn: number;
  readonly uploadedRatio: number;
  readonly targetRatio: number;
  readonly differencePercent: number;
};

export const getEtsyProductionWarnings = (
  order: EtsyPendingOrder,
): EtsyProductionWarning[] => (
  order.items.flatMap((item) => {
    const uploadedFile = item.fullSizeUploadedFile;
    if (
      !uploadedFile
      || !isPositiveNumber(uploadedFile.originalWidthPx)
      || !isPositiveNumber(uploadedFile.originalHeightPx)
      || !isPositiveNumber(item.matchedWidthIn)
      || !isPositiveNumber(item.matchedHeightIn)
    ) {
      return [];
    }

    const uploadedRatio = uploadedFile.originalWidthPx / uploadedFile.originalHeightPx;
    const targetRatio = item.matchedWidthIn / item.matchedHeightIn;
    const differencePercent = Math.abs(uploadedRatio - targetRatio) / targetRatio;

    if (differencePercent < ASPECT_RATIO_WARNING_THRESHOLD) {
      return [];
    }

    return [{
      itemId: item.id,
      itemTitle: item.title,
      uploadedWidthPx: uploadedFile.originalWidthPx,
      uploadedHeightPx: uploadedFile.originalHeightPx,
      targetWidthIn: item.matchedWidthIn,
      targetHeightIn: item.matchedHeightIn,
      uploadedRatio,
      targetRatio,
      differencePercent,
    }];
  })
);

export const getEtsyProductionWarningLabel = (
  warning: EtsyProductionWarning,
  t: TFunction,
): string => t(
  'integrations.etsy.toProduction.aspectRatioWarningItem',
  '{{title}}: uploaded full-size file is {{uploadedWidthPx}} x {{uploadedHeightPx}} px (shape ratio {{uploadedRatio}}), but selected Etsy print size is {{targetWidthIn}} x {{targetHeightIn}} in (shape ratio {{targetRatio}}).',
  {
    title: warning.itemTitle,
    uploadedWidthPx: formatPixels(warning.uploadedWidthPx),
    uploadedHeightPx: formatPixels(warning.uploadedHeightPx),
    targetWidthIn: formatInches(warning.targetWidthIn),
    targetHeightIn: formatInches(warning.targetHeightIn),
    uploadedRatio: formatRatio(warning.uploadedRatio),
    targetRatio: formatRatio(warning.targetRatio),
  },
);

export const getEtsyProductionWarningSummary = (
  warnings: ReadonlyArray<EtsyProductionWarning>,
  t: TFunction,
): string | null => {
  if (warnings.length === 0) {
    return null;
  }

  return t(
    'integrations.etsy.toProduction.aspectRatioWarning',
    'Some uploaded full-size files do not match the selected Etsy print size shape. This does not compare against the Etsy draft preview image: {{items}}',
    {
      items: warnings.map((warning) => getEtsyProductionWarningLabel(warning, t)).join(' · '),
    },
  );
};
